import { StringFieldProps } from '@components/Fields/StringField';
import useFormatter from '@hooks/useFormatter';
import { TextField } from '@material-ui/core';
import React from 'react';
import InputMask, { InputState, MaskOptions } from 'react-input-mask';

export type DocumentoMaskType = "both" | "cpf" | "cnpj";

const masks = {
    "cpf": "999.999.999-99",
    "cnpj": "99.999.999/9999-99"
}

type DocumentoFieldProps = StringFieldProps & {
    maskType: DocumentoMaskType
    dynamicLabels?: {
        cpf: string,
        cnpj: string
    }
}

function DocumentoFieldComponent ({ 
    disabled, 
    onChange, 
    maskType, 
    field, 
    onValueChanged, 
    updateFieldStateOnChange, 
    fullWidth,
    helperText,
    dynamicLabels,
    onEnter,
    label,
    onKeyDown,
    ...props 
}: DocumentoFieldProps) {
    const formatter = useFormatter();

    const _setMask = React.useCallback(() => {
        if (maskType === "both") {
            const l = (formatter.extractNumbers(field.value) || "").length;
            return l <= masks.cpf.length ? masks.cpf : masks.cnpj;
        }
        
        return masks[maskType];
    }, [field.value, formatter, maskType]);

    const __setLabel = React.useCallback(() => {
        if (dynamicLabels !== undefined) {
            if (maskType === "both") {
                return formatter.extractNumbers(field.value).length <= masks.cpf.length ? dynamicLabels.cpf : dynamicLabels.cnpj
            }

            return maskType === "cnpj" ? dynamicLabels.cnpj : dynamicLabels.cpf;
        }

        return label;
    }, [dynamicLabels, field.value, formatter, label, maskType]);

    const [mask, setMask] = React.useState<string>(_setMask());
    const [_value, _setValue] = React.useState(field.value);
    const [_label, _setLabel] = React.useState(__setLabel());

    const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        
        if (v !== _value) {
            _setValue(v);            
        }

    }, [_value]);

    const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" && onEnter !== undefined) {
            onEnter(e);
        }

        if (onKeyDown !== undefined) {
            onKeyDown(e);
        }
    }, [onEnter, onKeyDown]);

    const handleBeforeInput = React.useCallback((newState: InputState, oldState: InputState, userInput: string, maskOptions: MaskOptions,) => {
        if (maskType === "both") {
            const oldLength = (oldState.value || "").toString().length;
            const newLength = (newState.value || "").toString().length;
            let newMask: string | undefined = undefined;
            let newLabel: string | undefined = undefined;
    
            if (userInput !== null && mask !== masks.cnpj && oldLength === masks.cpf.length) { //então ele quer escrever mais um caracter, portanto passar para a próxima máscara imediatamente maior
                newLabel = dynamicLabels?.cnpj;
                newMask = masks.cnpj;
                newState.value += userInput;

                const lastPosition = masks.cnpj.length;

                newState.selection = {
                    start: lastPosition,
                    end: lastPosition
                }
            }
            else if (oldState.value !== "" && mask !== masks.cpf && newLength <= masks.cpf.length) {
                newLabel = dynamicLabels?.cpf;
                newMask = masks.cpf;
            }
    
            if (newMask) {
                setMask(newMask);
            }

            if (newLabel) {
                _setLabel(newLabel);
            }
        }

        return newState;
    }, [dynamicLabels?.cnpj, dynamicLabels?.cpf, mask, maskType]);

    // const setFormattedValue = React.useCallback((value) => {
    //     const documento = maskType === "cpf" ? formatter.toCpf(value) : formatter.toCnpj(value);
    //     _setValue(documento);        
    // }, [formatter, maskType]);

    //Reage à mudança do valor interno
    //Set o valor na ref
    //Chama o evento o change externo
    //Verifica se é para subir o estado automaticamente através da propriedade updateFieldStateOnChange
    React.useEffect(() => {
        field.setInputValue(_value);

        if (onValueChanged !== undefined) {
            onValueChanged(_value);
        }

        if (updateFieldStateOnChange === true && field.value !== _value) {
            field.setValue(_value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_value, onValueChanged, updateFieldStateOnChange]);

    React.useEffect(() => {
        setMask(_setMask());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maskType])

    React.useEffect(() => {
        _setLabel(__setLabel());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [label]);

    const setFormattedValue = React.useCallback((value: any) => {
        if (value === undefined || value === null || value === "") {
            _setValue("");
        }
        else {
            const documento = value.toString().replace(/\D/g, "");
            _setValue(documento);
        }
    }, []);
    

    //Reage à mudança do valor externo
    //Atualiza o valor interno pelo valor externo
    React.useEffect(() => {
        if (field.value !== _value) {
            _setValue(field.value);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value, setFormattedValue]);

    React.useEffect(() => {
        if (field.transition === "reset") {
            _setValue(field.initialValueRef?.current);
            field.clearTransition();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.transition]);

    return (
        <InputMask
            mask={mask}
            maskChar={null}
            alwaysShowMask={false}
            readOnly={props.InputProps?.readOnly}
            value={_value || ""}
            beforeMaskedValueChange={handleBeforeInput}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
        >
            {() => {
                return (
                    <TextField
                        {...props}
                        label={_label}
                        disabled={disabled}
                        name={field.name}
                        value={_value !== undefined && _value !== null ? _value : ""}
                        fullWidth={fullWidth !== false}
                        helperText={!!field.error ? field.error : helperText}
                        error={!!field.error}
                        onKeyDown={handleKeyDown}
                    />
                )
        }}
        </InputMask>
    );

}
export const DocumentoField = React.memo(DocumentoFieldComponent);